import { Check, Shield } from "lucide-react";

export const EnrollmentSection: React.FC<{ data: any; theme: any }> = ({
  data,
  theme,
}) => {
  if (!data) return null;

  const hasFullPayment = data.fullPayment?.enabled;
  const hasPaymentPlan = data.paymentPlan?.enabled;

  if (!hasFullPayment && !hasPaymentPlan) return null;

  return (
    <section
      id="enrollment"
      className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-white to-gray-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 text-gray-900">
            {data.sectionTitle || "Enroll Now"}
          </h2>
          {data.description && (
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {data.description}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto mb-8 sm:mb-12">
          {/* Full Payment */}
          {hasFullPayment && (
            <div
              className={`relative bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-6 sm:p-8 transition-all ${
                data.fullPayment.isPopular
                  ? "border-2 sm:border-4"
                  : "border border-gray-200"
              }`}
              style={{
                borderColor: data.fullPayment.isPopular
                  ? theme?.primaryColor || "#10B981"
                  : undefined,
              }}
            >
              {data.fullPayment.isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span
                    className="px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold text-white shadow-lg uppercase tracking-wide whitespace-nowrap"
                    style={{
                      backgroundColor: theme?.primaryColor || "#10B981",
                    }}
                  >
                    Best Value
                  </span>
                </div>
              )}

              <div className="text-center mb-6 mt-2">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  {data.fullPayment.label || "Full Payment"}
                </h3>
                <div className="mb-2">
                  <span
                    className="text-4xl sm:text-5xl font-bold"
                    style={{ color: theme?.primaryColor || "#10B981" }}
                  >
                    {data.fullPayment.displayPrice}
                  </span>
                </div>
                {data.fullPayment.savings && (
                  <p className="text-green-600 font-semibold text-base sm:text-lg">
                    Save {data.fullPayment.savings}
                  </p>
                )}
              </div>

              {data.fullPayment.features &&
                data.fullPayment.features.length > 0 && (
                  <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
                    {data.fullPayment.features.map(
                      (feature: string, i: number) => (
                        <li
                          key={i}
                          className="flex items-start gap-2.5 sm:gap-3"
                        >
                          <Check
                            size={18}
                            className="flex-shrink-0 mt-0.5"
                            style={{ color: theme?.primaryColor || "#10B981" }}
                          />
                          <span className="text-sm sm:text-base text-gray-700 leading-relaxed">
                            {feature}
                          </span>
                        </li>
                      ),
                    )}
                  </ul>
                )}

              <a
                href={data.fullPayment.buttonUrl || "#"}
                className="block w-full py-3 sm:py-4 text-center text-white font-bold rounded-lg text-base sm:text-lg shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ backgroundColor: theme?.primaryColor || "#10B981" }}
              >
                {data.fullPayment.buttonText || "Enroll Now"}
              </a>
            </div>
          )}

          {/* Payment Plan */}
          {hasPaymentPlan && (
            <div
              className={`relative bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-6 sm:p-8 transition-all ${
                data.paymentPlan.isPopular
                  ? "border-2 sm:border-4"
                  : "border border-gray-200"
              }`}
              style={{
                borderColor: data.paymentPlan.isPopular
                  ? theme?.primaryColor || "#10B981"
                  : undefined,
              }}
            >
              {data.paymentPlan.isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span
                    className="px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold text-white shadow-lg uppercase tracking-wide whitespace-nowrap"
                    style={{
                      backgroundColor: theme?.primaryColor || "#10B981",
                    }}
                  >
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6 mt-2">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  {data.paymentPlan.label || "Payment Plan"}
                </h3>
                <div className="mb-2">
                  <span
                    className="text-4xl sm:text-5xl font-bold"
                    style={{ color: theme?.primaryColor || "#10B981" }}
                  >
                    {data.paymentPlan.displayPrice}
                  </span>
                </div>
                <p className="text-gray-600 text-base sm:text-lg mb-1">
                  {data.paymentPlan.numberOfPayments} monthly payments
                </p>
                {data.paymentPlan.totalPrice && (
                  <p className="text-xs sm:text-sm text-gray-500">
                    {data.paymentPlan.totalPrice}
                  </p>
                )}
              </div>

              {data.paymentPlan.features &&
                data.paymentPlan.features.length > 0 && (
                  <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
                    {data.paymentPlan.features.map(
                      (feature: string, i: number) => (
                        <li
                          key={i}
                          className="flex items-start gap-2.5 sm:gap-3"
                        >
                          <Check
                            size={18}
                            className="flex-shrink-0 mt-0.5"
                            style={{ color: theme?.primaryColor || "#10B981" }}
                          />
                          <span className="text-sm sm:text-base text-gray-700 leading-relaxed">
                            {feature}
                          </span>
                        </li>
                      ),
                    )}
                  </ul>
                )}

              <a
                href={data.paymentPlan.buttonUrl || "#"}
                className="block w-full py-3 sm:py-4 text-center text-white font-bold rounded-lg text-base sm:text-lg shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ backgroundColor: theme?.primaryColor || "#10B981" }}
              >
                {data.paymentPlan.buttonText || "Start Payment Plan"}
              </a>
            </div>
          )}
        </div>

        {/* Guarantee */}
        {data.guarantee?.enabled && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 text-center border-2 border-gray-200">
              <div
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                style={{
                  backgroundColor: `${theme?.primaryColor || "#10B981"}20`,
                }}
              >
                <Shield
                  size={32}
                  className="sm:w-10 sm:h-10"
                  style={{ color: theme?.primaryColor || "#10B981" }}
                />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                {data.guarantee.title || "Money-Back Guarantee"}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {data.guarantee.description}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
